import type { TFunction } from 'i18next'
import {
  articlePath,
  articlesPath,
  localeMeta,
  locales,
  type Locale,
} from './i18n'
import type { PublicArticleDetail } from './articles/api'
import { articlesCopy } from './articles/copy'

export const siteUrl = 'https://sidewisp.com'
export const socialImageUrl = `${siteUrl}/sidewisp-og-v5.png`
export const articlesSeo = {
  title: 'AI Agent Operations Articles | Sidewisp',
  description: 'Practical guides for monitoring, diagnosing, and safely recovering AI agents in production.',
  path: '/articles/',
}

type HeadElement = {
  type: 'link' | 'meta' | 'script'
  props: Record<string, string>
  children?: string
}

export type LegalDocumentKind = 'terms' | 'privacy'

export const legalDocumentSeo: Record<LegalDocumentKind, { title: string; description: string; path: string }> = {
  terms: {
    title: 'Terms of Service | Sidewisp',
    description: 'Terms governing access to and use of Sidewisp, an AI-agent monitoring product operated by GOLEMWORKERS LLC.',
    path: '/terms/',
  },
  privacy: {
    title: 'Privacy Policy | Sidewisp',
    description: 'How GOLEMWORKERS LLC collects, uses, shares, and protects information for the Sidewisp website, waitlist, and product.',
    path: '/privacy/',
  },
}

const ogLocales: Record<Locale, string> = {
  en: 'en_US',
  es: 'es_ES',
  pt: 'pt_BR',
  de: 'de_DE',
  fr: 'fr_FR',
  it: 'it_IT',
  ru: 'ru_RU',
  zh: 'zh_CN',
  ja: 'ja_JP',
  ko: 'ko_KR',
}

export function localePath(locale: Locale): string {
  return locale === 'en' ? '/' : `/${locale}/`
}

export function localeUrl(locale: Locale): string {
  return `${siteUrl}${localePath(locale)}`
}

function meta(props: Record<string, string>): HeadElement {
  return { type: 'meta', props: { ...props, 'data-sidewisp-seo': 'true' } }
}

function link(props: Record<string, string>): HeadElement {
  return { type: 'link', props: { ...props, 'data-sidewisp-seo': 'true' } }
}

export function createStructuredData(locale: Locale, t: TFunction) {
  const canonicalUrl = localeUrl(locale)
  const language = localeMeta[locale].htmlLang
  const organizationId = `${siteUrl}/#organization`
  const websiteId = `${siteUrl}/#website`
  const applicationId = `${siteUrl}/#application`

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': organizationId,
        name: 'Sidewisp',
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/brand/sidewisp-logo.svg`,
        },
        email: 'info@sidewisp.com',
      },
      {
        '@type': 'WebSite',
        '@id': websiteId,
        url: siteUrl,
        name: 'Sidewisp',
        description: t('seo.description'),
        inLanguage: language,
        publisher: { '@id': organizationId },
      },
      {
        '@type': 'WebApplication',
        '@id': applicationId,
        name: 'Sidewisp',
        url: canonicalUrl,
        description: t('seo.description'),
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        inLanguage: language,
        image: socialImageUrl,
        publisher: { '@id': organizationId },
        isPartOf: { '@id': websiteId },
        keywords: [
          'AI agent monitoring',
          'AI agent health',
          'AI agent observability',
          'AI agent recovery',
          'OpenClaw monitoring',
          'Claude Code monitoring',
          'Codex monitoring',
        ],
        featureList: [
          t('checks.items.availability.title'),
          t('checks.items.execution.title'),
          t('checks.items.memory.title'),
          t('checks.items.tools.title'),
          t('checks.items.outcome.title'),
          t('checks.items.cost.title'),
        ],
      },
      {
        '@type': 'WebPage',
        '@id': `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: t('seo.title'),
        description: t('seo.description'),
        inLanguage: language,
        isPartOf: { '@id': websiteId },
        about: { '@id': applicationId },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: socialImageUrl,
          width: 1200,
          height: 630,
        },
      },
    ],
  }
}

export function createSeoHeadElements(locale: Locale, t: TFunction): HeadElement[] {
  const canonicalUrl = localeUrl(locale)
  const socialTitle = t('seo.socialTitle')
  const socialDescription = t('seo.socialDescription')
  const imageAlt = t('seo.imageAlt')

  return [
    link({ rel: 'canonical', href: canonicalUrl }),
    ...locales.map((item) => link({
      rel: 'alternate',
      hreflang: localeMeta[item].htmlLang,
      href: localeUrl(item),
      'data-sidewisp-hreflang': 'true',
    })),
    link({ rel: 'alternate', hreflang: 'x-default', href: localeUrl('en'), 'data-sidewisp-hreflang': 'true' }),
    meta({ name: 'description', content: t('seo.description') }),
    meta({ name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' }),
    meta({ name: 'application-name', content: 'Sidewisp' }),
    meta({ property: 'og:type', content: 'website' }),
    meta({ property: 'og:site_name', content: 'Sidewisp' }),
    meta({ property: 'og:locale', content: ogLocales[locale] }),
    ...locales.filter((item) => item !== locale).map((item) => meta({
      property: 'og:locale:alternate',
      content: ogLocales[item],
    })),
    meta({ property: 'og:url', content: canonicalUrl }),
    meta({ property: 'og:title', content: socialTitle }),
    meta({ property: 'og:description', content: socialDescription }),
    meta({ property: 'og:image', content: socialImageUrl }),
    meta({ property: 'og:image:secure_url', content: socialImageUrl }),
    meta({ property: 'og:image:type', content: 'image/png' }),
    meta({ property: 'og:image:width', content: '1200' }),
    meta({ property: 'og:image:height', content: '630' }),
    meta({ property: 'og:image:alt', content: imageAlt }),
    meta({ name: 'twitter:card', content: 'summary_large_image' }),
    meta({ name: 'twitter:title', content: socialTitle }),
    meta({ name: 'twitter:description', content: socialDescription }),
    meta({ name: 'twitter:image', content: socialImageUrl }),
    meta({ name: 'twitter:image:alt', content: imageAlt }),
    {
      type: 'script',
      props: { type: 'application/ld+json', id: 'sidewisp-structured-data', 'data-sidewisp-seo': 'true' },
      children: JSON.stringify(createStructuredData(locale, t)).replace(/</g, '\\u003c'),
    },
  ]
}

export function createLegalSeoHeadElements(kind: LegalDocumentKind): HeadElement[] {
  const document = legalDocumentSeo[kind]
  const canonicalUrl = `${siteUrl}${document.path}`

  return [
    link({ rel: 'canonical', href: canonicalUrl }),
    meta({ name: 'description', content: document.description }),
    meta({ name: 'robots', content: 'index, follow, max-snippet:-1' }),
    meta({ property: 'og:type', content: 'website' }),
    meta({ property: 'og:site_name', content: 'Sidewisp' }),
    meta({ property: 'og:url', content: canonicalUrl }),
    meta({ property: 'og:title', content: document.title }),
    meta({ property: 'og:description', content: document.description }),
    meta({ name: 'twitter:card', content: 'summary' }),
    meta({ name: 'twitter:title', content: document.title }),
    meta({ name: 'twitter:description', content: document.description }),
  ]
}

export function createArticlesSeoHeadElements(locale: Locale): HeadElement[] {
  const copy = articlesCopy[locale].seo
  const canonicalUrl = `${siteUrl}${articlesPath(locale)}`
  const language = localeMeta[locale].htmlLang
  return [
    link({ rel: 'canonical', href: canonicalUrl }),
    ...locales.map((item) => link({
      rel: 'alternate',
      hreflang: localeMeta[item].htmlLang,
      href: `${siteUrl}${articlesPath(item)}`,
      'data-sidewisp-hreflang': 'true',
    })),
    link({
      rel: 'alternate',
      hreflang: 'x-default',
      href: `${siteUrl}${articlesPath('en')}`,
      'data-sidewisp-hreflang': 'true',
    }),
    meta({ name: 'description', content: copy.description }),
    meta({ name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1' }),
    meta({ property: 'og:type', content: 'website' }),
    meta({ property: 'og:site_name', content: 'Sidewisp' }),
    meta({ property: 'og:locale', content: ogLocales[locale] }),
    meta({ property: 'og:url', content: canonicalUrl }),
    meta({ property: 'og:title', content: copy.title }),
    meta({ property: 'og:description', content: copy.description }),
    meta({ property: 'og:image', content: socialImageUrl }),
    meta({ name: 'twitter:card', content: 'summary_large_image' }),
    meta({ name: 'twitter:title', content: copy.title }),
    meta({ name: 'twitter:description', content: copy.description }),
    meta({ name: 'twitter:image', content: socialImageUrl }),
    {
      type: 'script',
      props: { type: 'application/ld+json', id: 'sidewisp-articles-structured-data', 'data-sidewisp-seo': 'true' },
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: copy.title,
        description: copy.description,
        url: canonicalUrl,
        inLanguage: language,
        isPartOf: { '@id': `${siteUrl}/#website` },
        publisher: { '@id': `${siteUrl}/#organization` },
      }).replace(/</g, '\\u003c'),
    },
  ]
}

export function createArticleSeoHeadElements(article: PublicArticleDetail, locale: Locale): HeadElement[] {
  const canonicalUrl = `${siteUrl}${articlePath(locale, article.slug)}`
  const language = localeMeta[locale].htmlLang
  const description = article.excerpt || article.body.replace(/[#*_`() >~-]+/g, ' ').replaceAll('[', ' ').replaceAll(']', ' ').replace(/\s+/g, ' ').trim().slice(0, 160)
  const imageUrl = article.headerImageUrl
    ? new URL(article.headerImageUrl, siteUrl).toString()
    : socialImageUrl
  const title = `${article.title} | Sidewisp`
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description,
    url: canonicalUrl,
    image: imageUrl,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    inLanguage: language,
    author: { '@type': 'Organization', name: 'Sidewisp', url: siteUrl },
    publisher: {
      '@type': 'Organization',
      name: 'Sidewisp',
      url: siteUrl,
      logo: { '@type': 'ImageObject', url: `${siteUrl}/brand/sidewisp-logo.svg` },
    },
    mainEntityOfPage: canonicalUrl,
  }
  return [
    link({ rel: 'canonical', href: canonicalUrl }),
    meta({ name: 'description', content: description }),
    meta({ name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1' }),
    meta({ property: 'og:type', content: 'article' }),
    meta({ property: 'og:site_name', content: 'Sidewisp' }),
    meta({ property: 'og:locale', content: ogLocales[locale] }),
    meta({ property: 'og:url', content: canonicalUrl }),
    meta({ property: 'og:title', content: title }),
    meta({ property: 'og:description', content: description }),
    meta({ property: 'og:image', content: imageUrl }),
    meta({ property: 'article:published_time', content: article.publishedAt }),
    meta({ property: 'article:modified_time', content: article.updatedAt }),
    meta({ name: 'twitter:card', content: 'summary_large_image' }),
    meta({ name: 'twitter:title', content: title }),
    meta({ name: 'twitter:description', content: description }),
    meta({ name: 'twitter:image', content: imageUrl }),
    {
      type: 'script',
      props: { type: 'application/ld+json', id: 'sidewisp-article-structured-data', 'data-sidewisp-seo': 'true' },
      children: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
    },
  ]
}

function syncSeoElements(title: string, language: string, elements: HeadElement[]) {
  documentElementLanguage(language)
  window.document.title = title
  window.document.head.querySelectorAll('[data-sidewisp-seo]').forEach((node) => node.remove())
  elements.forEach((element) => {
    const node = window.document.createElement(element.type)
    Object.entries(element.props).forEach(([name, value]) => node.setAttribute(name, value))
    if (element.children) node.textContent = element.children
    window.document.head.append(node)
  })
}

export function syncDocumentSeo(locale: Locale, t: TFunction) {
  document.documentElement.lang = localeMeta[locale].htmlLang
  document.title = t('seo.title')
  document.head.querySelectorAll('[data-sidewisp-seo]').forEach((node) => node.remove())

  createSeoHeadElements(locale, t).forEach((element) => {
    const node = document.createElement(element.type)
    Object.entries(element.props).forEach(([name, value]) => node.setAttribute(name, value))
    if (element.children) node.textContent = element.children
    document.head.append(node)
  })
}

export function syncLegalDocumentSeo(kind: LegalDocumentKind) {
  const document = legalDocumentSeo[kind]
  syncSeoElements(document.title, 'en', createLegalSeoHeadElements(kind))
}

export function syncArticlesSeo(locale: Locale) {
  const copy = articlesCopy[locale].seo
  syncSeoElements(copy.title, localeMeta[locale].htmlLang, createArticlesSeoHeadElements(locale))
}

export function syncArticleSeo(article: PublicArticleDetail, locale: Locale) {
  syncSeoElements(
    `${article.title} | Sidewisp`,
    localeMeta[locale].htmlLang,
    createArticleSeoHeadElements(article, locale),
  )
}

function documentElementLanguage(language: string) {
  window.document.documentElement.lang = language
}
